import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Icon1 from "@assets/icons/Icon";
import type { DocOwner } from "@components/ui/DocThumbnail";
import { stageDocumentInClientChat } from "@utils/sendDocToChat";

type MenuDoc = { id: string; name: string; type?: string; gcs_path?: string };

/** Clic droit sur un document de l'ordre → « Envoyer au client » (Héric 2026-10-16).
 *  `onDocContextMenu(doc)` se branche sur la ligne ; `menu` se rend une fois par liste. */
export function useDocSendMenu(owner: DocOwner, discussionId: string | undefined) {
  const [at, setAt] = useState<{ x: number; y: number; doc: MenuDoc } | null>(null);

  useEffect(() => {
    if (!at) return;
    const close = () => setAt(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("mousedown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [at]);

  const onDocContextMenu = (doc: MenuDoc) => (e: React.MouseEvent) => {
    if (!doc.id) return;
    e.preventDefault();
    e.stopPropagation();
    // Le menu fait ~240 px : on le garde dans la fenêtre.
    setAt({ x: Math.min(e.clientX, window.innerWidth - 250), y: Math.min(e.clientY, window.innerHeight - 60), doc });
  };

  const menu = at
    ? createPortal(
        // Au-dessus des modales (10000), cf. la fiche ordre ouverte en modale.
        <div
          className="fixed bg-white border border-neutral-200 rounded-lg shadow-lg py-1"
          style={{ left: at.x, top: at.y, zIndex: 10001, minWidth: 240 }}
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            className="w-full flex flex-row items-center gap-2 px-3 py-2 text-h3 text-left hover:bg-neutral-50 disabled:opacity-40 disabled:hover:bg-transparent"
            disabled={!discussionId}
            title={discussionId ? undefined : "Pas de conversation client pour cet ordre"}
            onClick={() => {
              const doc = at.doc;
              setAt(null);
              stageDocumentInClientChat(doc, owner, discussionId);
            }}
          >
            <Icon1 name="send" size={14} color="#525252" />
            Envoyer au client dans la conversation
          </button>
        </div>,
        document.body,
      )
    : null;

  return { onDocContextMenu, menu };
}
